/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, KPICard, Pill } from "../components/Bits";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StartBatchDialog } from "../components/StartBatchDialog";
import { toast } from "sonner";
import {
    BroadcastIcon, PauseIcon, ArrowLeftIcon, PhoneCallIcon, TestTubeIcon, ArrowsClockwiseIcon, RocketIcon,
} from "@phosphor-icons/react";
import { formatDuration, formatRelative, OUTCOME_COLORS, OUTCOME_LABELS } from "../lib/format";

export default function CampaignDetail() {
    const { id } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [calls, setCalls] = useState([]);
    const [testCalls, setTestCalls] = useState([]);
    const [busy, setBusy] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [testPhone, setTestPhone] = useState("");
    const [testName, setTestName] = useState("Me");
    const [showStartDialog, setShowStartDialog] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        const [c, cls, tc] = await Promise.all([
            api.get(`/campaigns/${id}`),
            api.get(`/calls`, { params: { campaign_id: id, limit: 50 } }),
            api.get(`/campaigns/${id}/test-calls`),
        ]);
        setCampaign(c.data);
        setCalls(cls.data);
        setTestCalls(tc.data);
    };

    useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [id]);

    const start = async (override = false) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/campaigns/${id}/start`, { batch_size: 5, override_business_hours: override });
            const mode = data.mode === "vapi" ? "real (Vapi)" : "mock";
            toast.success(`Placed ${data.calls_completed} ${mode} call${data.calls_completed === 1 ? "" : "s"}`);
            if (data.errors && data.errors.length) {
                toast.error(`${data.errors.length} call(s) failed`);
            }
            setShowStartDialog(false);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Start failed";
            if (e?.response?.status === 400 && typeof detail === "string" && detail.includes("Outside business hours")) {
                if (window.confirm(`${detail}\n\nOverride and place calls anyway?`)) {
                    setBusy(false);
                    return start(true);
                }
                toast.error(detail);
            } else {
                toast.error(detail);
            }
        } finally { setBusy(false); }
    };

    const schedule = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/campaigns/${id}/schedule`, { batch_size: 5 });
            const when = new Date(data.scheduled_for).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
            toast.success(`Scheduled to run ${when}`);
            setShowStartDialog(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Schedule failed");
        } finally { setBusy(false); }
    };

    const pause = async () => {
        await api.post(`/campaigns/${id}/pause`);
        toast.success("Paused");
        await load();
    };

    const placeTestCall = async () => {
        const phone = testPhone.trim();
        if (!phone) { toast.error("Enter your phone number"); return; }
        setTestBusy(true);
        try {
            const { data } = await api.post(`/campaigns/${id}/test-call`, { phone, name: testName || "Me" });
            toast.success(`Calling ${data.phone}…`, { description: "Your phone should ring in a few seconds." });
            setShowTest(false);
            // Refresh test call list quickly
            setTimeout(load, 2000);
        } catch (e) {
            const detail = e?.response?.data?.detail || "Test call failed";
            toast.error(typeof detail === "string" ? detail : "Test call failed");
        } finally { setTestBusy(false); }
    };

    if (!campaign) return <div className="p-8 text-[13px] text-[var(--text-secondary)]">Loading…</div>;

    const conv = campaign.total_calls ? Math.round((campaign.appointments_booked / campaign.total_calls) * 100) : 0;

    return (
        <div className="pb-12">
            <PageHeader
                overline={
                    campaign.status === "scheduled" && campaign.scheduled_for
                        ? `CAMPAIGN · SCHEDULED FOR ${new Date(campaign.scheduled_for).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).toUpperCase()}`
                        : `CAMPAIGN · ${campaign.status.toUpperCase()}`
                }
                title={campaign.name}
                description={`Voice ${campaign.voice_config?.voice} · pace ${campaign.voice_config?.pace} · ${campaign.business_hours?.timezone}`}
                testid="campaign-detail-header"
                actions={
                    <>
                        <button type="button" onClick={() => navigate(-1)} className="btn-ghost px-3 py-2 text-[13px] flex items-center gap-1.5 whitespace-nowrap" data-testid="back-button">
                            <ArrowLeftIcon size={14} weight="bold" /> Back
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowTest(true)}
                            className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2 whitespace-nowrap border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--surface-active)]"
                            data-testid="open-test-call"
                        >
                            <TestTubeIcon size={14} weight="bold" /> Test call
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowStartDialog(true)}
                            disabled={busy}
                            className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2 whitespace-nowrap disabled:opacity-50"
                            data-testid="start-batch-detail"
                        >
                            <BroadcastIcon size={14} weight="bold" /> {busy ? "Working…" : "Start batch"}
                        </button>
                        {(campaign.status === "active" || campaign.status === "scheduled") && (
                            <button type="button" onClick={pause} className="btn-ghost px-3 py-2.5 text-[13px] flex items-center gap-1.5 whitespace-nowrap" data-testid="pause-detail">
                                <PauseIcon size={14} weight="bold" /> {campaign.status === "scheduled" ? "Cancel" : "Pause"}
                            </button>
                        )}
                    </>
                }
            />

            <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPICard label="TOTAL CALLS" value={campaign.total_calls} testid="kpi-total-calls" />
                <KPICard label="APPOINTMENTS" value={campaign.appointments_booked} accent testid="kpi-appointments-detail" />
                <KPICard label="WHITEPAPERS" value={campaign.whitepapers_sent} testid="kpi-whitepapers-detail" />
                <KPICard label="BOOK RATE" value={`${conv}%`} testid="kpi-book-rate" />
            </div>

            {/* Test calls panel */}
            <div className="px-8">
                <div className="swiss-section border-l-[3px] border-l-[var(--accent)] mb-3" data-testid="test-call-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="overline text-[var(--accent)] flex items-center gap-1.5">
                                <TestTubeIcon size={11} weight="bold" /> DRY RUN
                            </div>
                            <h3 className="text-xl font-semibold tracking-tight mt-0.5">Test before you go live</h3>
                            <p className="text-[12px] text-[var(--text-secondary)] mt-1 max-w-xl">
                                Place a real Vapi call to your own number using this campaign's script + voice config.
                                Test calls don't touch your prospect statuses or campaign metrics — iterate freely.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowTest(true)}
                            className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2 whitespace-nowrap"
                            data-testid="open-test-call-panel"
                        >
                            <PhoneCallIcon size={14} weight="bold" /> Call my phone
                        </button>
                    </div>
                    {testCalls.length === 0 ? (
                        <div className="px-5 py-6 text-center text-[var(--text-secondary)] text-[13px]" data-testid="test-call-empty">
                            No test calls yet. Tip: after a test, tweak the Script and try again — repeat until satisfied.
                        </div>
                    ) : (
                        <ul className="grid-borders" data-testid="test-call-list">
                            {testCalls.map((c) => (
                                <li key={c.id} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--surface-active)]">
                                    <TestTubeIcon size={16} weight="bold" className="text-[var(--accent)] shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/calls/${c.id}`} className="font-semibold text-[14px] hover:underline truncate block" data-testid={`test-call-${c.id}`}>
                                            {c.prospect_name}
                                        </Link>
                                        <div className="text-[12px] text-[var(--text-secondary)] font-mono truncate">
                                            {formatDuration(c.duration_sec)} · sentiment {c.sentiment_label} · {formatRelative(c.call_timestamp)}
                                        </div>
                                    </div>
                                    <Pill color={OUTCOME_COLORS[c.outcome] || "#111827"}>{OUTCOME_LABELS[c.outcome] || c.outcome}</Pill>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Real campaign call log */}
                <div className="swiss-section">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
                        <div>
                            <div className="overline">CALL LOG</div>
                            <h3 className="text-xl font-semibold mt-0.5">Latest 50 calls</h3>
                        </div>
                        <span className="flex items-center gap-2 overline text-[var(--text-secondary)]">
                            <ArrowsClockwiseIcon size={11} weight="bold" /> Auto-refresh 8s
                        </span>
                    </div>
                    {calls.length === 0 ? (
                        <div className="p-10 text-center text-[var(--text-secondary)] text-[13px]">
                            No live calls yet — once you're happy with a test, click <span className="font-mono">Start batch</span>.
                        </div>
                    ) : (
                        <ul className="grid-borders" data-testid="campaign-call-list">
                            {calls.map((c) => (
                                <li key={c.id} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--surface-active)]">
                                    <PhoneCallIcon size={16} weight="bold" className="text-[var(--text-secondary)]" />
                                    <div className="min-w-0 flex-1">
                                        <Link to={`/calls/${c.id}`} className="font-semibold text-[14px] hover:underline truncate block" data-testid={`campaign-call-${c.id}`}>
                                            {c.prospect_name}
                                        </Link>
                                        <div className="text-[12px] text-[var(--text-secondary)] font-mono truncate">
                                            {c.prospect_company || "—"} · {formatDuration(c.duration_sec)} · sentiment {c.sentiment_label} · {formatRelative(c.call_timestamp)}
                                        </div>
                                    </div>
                                    <Pill color={OUTCOME_COLORS[c.outcome] || "#111827"}>{OUTCOME_LABELS[c.outcome] || c.outcome}</Pill>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Test call dialog */}
            <Dialog open={showTest} onOpenChange={setShowTest}>
                <DialogContent className="rounded-[2px] sm:max-w-[480px]" data-testid="test-call-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><TestTubeIcon size={18} weight="bold" /> Place a test call</DialogTitle>
                        <DialogDescription>
                            We'll call the number you enter using <span className="font-mono">{campaign.voice_config?.voice}</span> voice, <span className="font-mono">{campaign.voice_config?.pace}</span> pace, and the campaign's exact script — same as a real prospect call. Result is logged here but excluded from campaign stats.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline mb-1.5 block">Your phone number</label>
                            <input
                                value={testPhone}
                                onChange={(e) => setTestPhone(e.target.value)}
                                placeholder="+13219997374"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[14px] mono"
                                data-testid="test-call-phone"
                                autoFocus
                            />
                            <p className="text-[11px] text-[var(--text-secondary)] mt-1">Include the country code (e.g. <span className="mono">+1</span>).</p>
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Display name (used in greeting)</label>
                            <input
                                value={testName}
                                onChange={(e) => setTestName(e.target.value)}
                                placeholder="Me"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                data-testid="test-call-name"
                            />
                        </div>
                        <div className="bg-[var(--surface-active)] p-3 text-[11px] text-[var(--text-secondary)]">
                            <span className="overline mb-1 block text-[var(--text-primary)]">FIRST MESSAGE PREVIEW</span>
                            <div className="font-mono">
                                "{(campaign.script_first_message || "Hi {name}, this is Alex from your team.").replace("{name}", testName || "Me")}"
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setShowTest(false)} className="btn-ghost px-4 py-2 text-[13px]" data-testid="test-call-cancel">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={placeTestCall}
                            disabled={testBusy}
                            className="btn-primary px-4 py-2 text-[13px] flex items-center gap-2 disabled:opacity-50"
                            data-testid="test-call-submit"
                        >
                            <RocketIcon size={14} weight="bold" /> {testBusy ? "Calling…" : "Call my phone"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <StartBatchDialog
                campaign={showStartDialog ? campaign : null}
                onClose={() => setShowStartDialog(false)}
                onStartNow={() => start()}
                onSchedule={schedule}
                busy={busy}
            />
        </div>
    );
}
